import { BlockType } from '../terrain'
import { InventoryItemType, ItemType } from '../items'
import type { InventorySlot } from './index'

export type CraftingMode = 'personal' | 'table'

export interface Recipe {
  mode: CraftingMode
  pattern: (InventoryItemType | null)[]
  resultType: InventoryItemType
  resultCount: number
}

const personalSingles = (
  input: InventoryItemType,
  resultType: InventoryItemType,
  resultCount: number
): Recipe[] => {
  return [0, 1, 2, 3].map(index => {
    const pattern = new Array<InventoryItemType | null>(4).fill(null)
    pattern[index] = input
    return {
      mode: 'personal',
      pattern,
      resultType,
      resultCount
    }
  })
}

export const recipes: Recipe[] = [
  ...personalSingles(BlockType.tree, BlockType.wood, 4),
  ...personalSingles(BlockType.sand, BlockType.glass, 1),
  ...personalSingles(BlockType.wood, ItemType.stick, 4),
  {
    mode: 'personal',
    pattern: [BlockType.leaf, BlockType.leaf, BlockType.leaf, BlockType.leaf],
    resultType: ItemType.stick,
    resultCount: 1
  },
  {
    mode: 'personal',
    pattern: [BlockType.wood, BlockType.wood, BlockType.wood, BlockType.wood],
    resultType: BlockType.craftingTable,
    resultCount: 1
  },
  {
    mode: 'personal',
    pattern: [
      BlockType.cobblestone,
      BlockType.cobblestone,
      BlockType.cobblestone,
      BlockType.cobblestone
    ],
    resultType: BlockType.stone,
    resultCount: 4
  },
  {
    mode: 'personal',
    pattern: [BlockType.stone, BlockType.stone, BlockType.stone, BlockType.stone],
    resultType: BlockType.quartz,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.wood, BlockType.wood, BlockType.wood,
      null, ItemType.stick, null,
      null, ItemType.stick, null
    ],
    resultType: ItemType.woodenPickaxe,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.cobblestone, BlockType.cobblestone, BlockType.cobblestone,
      null, ItemType.stick, null,
      null, ItemType.stick, null
    ],
    resultType: ItemType.stonePickaxe,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      ItemType.gunpowder, BlockType.sand, ItemType.gunpowder,
      BlockType.sand, ItemType.gunpowder, BlockType.sand,
      ItemType.gunpowder, BlockType.sand, ItemType.gunpowder
    ],
    resultType: BlockType.tnt,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.quartz, null, BlockType.quartz,
      null, BlockType.quartz, null,
      null, null, null
    ],
    resultType: ItemType.bucket,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      ItemType.rawBeef, null, null,
      null, BlockType.coal, null,
      null, null, null
    ],
    resultType: ItemType.cookedBeef,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.glass, BlockType.glass, BlockType.glass,
      BlockType.glass, null, BlockType.glass,
      BlockType.glass, BlockType.glass, BlockType.glass
    ],
    resultType: BlockType.diamond,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.leaf, BlockType.leaf, BlockType.leaf,
      BlockType.leaf, BlockType.leaf, BlockType.leaf,
      BlockType.leaf, BlockType.leaf, BlockType.leaf
    ],
    resultType: BlockType.tree,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.dirt, BlockType.dirt, BlockType.dirt,
      BlockType.dirt, BlockType.dirt, BlockType.dirt,
      BlockType.dirt, BlockType.dirt, BlockType.dirt
    ],
    resultType: BlockType.grass,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.sand, BlockType.sand, BlockType.sand,
      BlockType.sand, BlockType.sand, BlockType.sand,
      BlockType.sand, BlockType.sand, BlockType.sand
    ],
    resultType: BlockType.quartz,
    resultCount: 1
  },
  {
    mode: 'table',
    pattern: [
      BlockType.quartz, BlockType.quartz, BlockType.quartz,
      BlockType.quartz, BlockType.quartz, BlockType.quartz,
      BlockType.quartz, BlockType.quartz, BlockType.quartz
    ],
    resultType: BlockType.diamond,
    resultCount: 1
  }
]

export function checkRecipes(
  craftingSlots: (InventorySlot | null)[],
  mode: CraftingMode
): Recipe | null {
  const size = mode === 'table' ? 9 : 4
  if (craftingSlots.length !== size) return null

  for (const recipe of recipes) {
    if (recipe.mode !== mode) continue

    let matches = true
    for (let i = 0; i < size; i++) {
      const slotType = craftingSlots[i] ? craftingSlots[i]!.type : null
      if (recipe.pattern[i] !== slotType) {
        matches = false
        break
      }
    }
    if (matches) return recipe
  }

  return null
}
